import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, User, Sun, Moon, LogOut } from 'lucide-react';
import { cn } from '../lib/utils';
import { auth } from '../lib/firebase';
import { onAuthStateChanged, User as FirebaseUser, signOut } from 'firebase/auth';

export function ThemeToggle() {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    if (theme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  return (
    <button
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      className="p-2 transition-all hover:text-gold text-theme-text"
      aria-label="Toggle Theme"
    >
      {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Dashboard', path: '/dashboard' },
    { name: 'Audit', path: '/audit' },
    { name: 'Curation', path: '/curation' },
    { name: 'Library', path: '/library' },
    { name: 'Vault', path: '/vault' },
    { name: 'Philosophy', path: '/philosophy' },
  ];

  return (
    <header className={cn(
      "header-glass",
      isScrolled ? "header-scrolled" : "header-transparent"
    )}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-full">
        <Link to="/" className="flex items-center min-w-[48px]">
          <img 
            src="/logo.png" 
            alt="She Deserves Better" 
            className="h-12 w-auto object-contain block"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center space-x-12">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cn(
                "text-[0.75rem] uppercase tracking-[0.15em] font-medium transition-all hover:opacity-100",
                location.pathname === link.path ? "text-gold opacity-100" : "text-theme-text opacity-80"
              )}
            >
              {link.name}
            </Link>
          ))}
          {user ? (
            <button
              onClick={handleLogout}
              className="text-[0.75rem] uppercase tracking-[0.15em] font-medium text-taupe hover:text-gold transition-colors flex items-center space-x-2"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          ) : (
            <Link
              to="/auth"
              className="btn-outline h-auto py-2 px-6"
            >
              Member Access
            </Link>
          )}
          <ThemeToggle />
        </nav>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden text-theme-text"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 bg-theme-bg z-40 flex flex-col items-center justify-center space-y-8"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-2xl font-serif tracking-widest uppercase text-theme-text hover:text-gold"
              >
                {link.name}
              </Link>
            ))}
            {user ? (
              <button
                onClick={handleLogout}
                className="text-2xl font-serif tracking-widest uppercase text-theme-text hover:text-gold flex items-center space-x-3"
              >
                <LogOut className="h-6 w-6" />
                <span>Logout</span>
              </button>
            ) : (
              <Link
                to="/auth"
                className="btn-gold"
              >
                Member Login
              </Link>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-theme-bg border-t border-theme-border py-16">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-12">
        <div className="flex flex-col">
          <img 
            src="/logo.png" 
            alt="She Deserves Better" 
            className="h-16 w-auto object-contain mb-8 transition-all [.light_&]:invert"
          />
          <h3 className="text-xl font-bold mb-2 tracking-[0.3em] text-theme-text">She Deserves Better</h3>
          <span className="text-[9px] uppercase tracking-[0.4em] text-gold block mb-6 font-semibold">Know Yourself. Choose Better.</span>
          <p className="text-taupe text-sm leading-relaxed max-w-xs">
            A confidential executive briefing for the architectural design of high-status relationships.
          </p>
        </div>
        <div className="flex flex-col space-y-4">
          <span className="text-[10px] uppercase tracking-widest text-gold mb-2">Navigation</span>
          <Link to="/philosophy" className="text-sm text-theme-text/70 hover:text-theme-text">Philosophy</Link>
          <Link to="/library" className="text-sm text-theme-text/70 hover:text-theme-text">The Archive</Link>
          <Link to="/vault" className="text-sm text-theme-text/70 hover:text-theme-text">VIP Vault</Link>
        </div>
        <div className="flex flex-col space-y-4">
          <span className="text-[10px] uppercase tracking-widest text-gold mb-2">Confidentiality</span>
          <p className="text-xs text-taupe leading-relaxed">
            All consultations and archives are under strict non-disclosure. Your privacy is our architectural foundation.
          </p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-16 pt-8 border-t border-theme-border flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
        <div className="flex flex-col items-center md:items-start space-y-1">
          <p className="text-[10px] uppercase tracking-widest text-taupe/60">
            © {new Date().getFullYear()} SDB Clinical Luxury. All Rights Reserved.
          </p>
          <div className="flex items-center space-x-2 text-[8px] uppercase tracking-widest text-taupe/40 font-mono">
            <span>SDB-OS v1.2</span>
            <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>Secure Line Active</span>
          </div>
        </div>
        <div className="flex space-x-8 text-[10px] uppercase tracking-widest text-taupe/60">
          <a href="#" className="hover:text-gold">Privacy</a>
          <a href="#" className="hover:text-gold">Terms</a>
        </div>
      </div>
    </footer>
  );
}
